(() => {
  if (window.MaxlerFlavourPicker) return;

  class MaxlerFlavourPicker {
    constructor(select, {signal, labels = {}, context = select.form} = {}) {
      if (!(select instanceof HTMLSelectElement)) throw new TypeError('A flavour select is required.');
      this.select = select;
      this.context = context;
      this.signal = signal || new AbortController().signal;
      this.labels = {
        available: 'Available to buy',
        unavailable: 'Currently unavailable',
        empty: 'No flavours for this size',
        hint: '',
        ...labels
      };
      this.isOpen = false;
      this.activeIndex = -1;
      this.rows = [];
      if (this.signal.aborted) return;

      this.original = {
        id: select.id,
        hidden: select.hidden,
        ariaHidden: select.getAttribute('aria-hidden'),
        tabIndex: select.getAttribute('tabindex')
      };
      if (!select.id) select.id = 'maxler-flavour-' + Math.random().toString(36).slice(2);
      const id = select.id + '-picker';
      this.wrapper = document.createElement('div');
      this.wrapper.className = 'maxler-qp-picker';
      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.id = id + '-trigger';
      this.trigger.className = 'maxler-qp-picker-trigger';
      this.trigger.dataset.qpPickerTrigger = '';
      this.trigger.setAttribute('role', 'combobox');
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.setAttribute('aria-controls', id + '-panel');
      this.value = document.createElement('span');
      this.value.className = 'maxler-qp-picker-value';
      const chevron = document.createElement('span');
      chevron.className = 'maxler-qp-picker-chevron';
      chevron.setAttribute('aria-hidden', 'true');
      this.trigger.append(this.value, chevron);

      this.panel = document.createElement('div');
      this.panel.id = id + '-panel';
      this.panel.className = 'maxler-qp-picker-panel';
      this.panel.dataset.qpPickerPanel = '';
      this.panel.setAttribute('role', 'listbox');
      this.panel.hidden = true;
      this.hasPopover = typeof this.panel.showPopover === 'function';
      if (this.hasPopover) this.panel.setAttribute('popover', 'manual');
      this.wrapper.append(this.trigger, this.panel);
      select.insertAdjacentElement('afterend', this.wrapper);

      this.labelElements = [...select.labels].map((label, index) => {
        const originalId = label.id;
        const originalFor = label.getAttribute('for');
        if (!label.id) label.id = id + '-label-' + index;
        if (originalFor === select.id) label.htmlFor = this.trigger.id;
        return {label, originalId, originalFor};
      });
      const labelledBy = select.getAttribute('aria-labelledby') || this.labelElements.map(({label}) => label.id).join(' ');
      if (labelledBy) {
        this.trigger.setAttribute('aria-labelledby', labelledBy);
        this.panel.setAttribute('aria-labelledby', labelledBy);
      } else {
        const name = select.getAttribute('aria-label') || select.name || 'Flavour';
        this.trigger.setAttribute('aria-label', name);
        this.panel.setAttribute('aria-label', name);
      }
      const descriptions = [select.getAttribute('aria-describedby')];
      if (this.labels.hint) {
        const hint = document.createElement('span');
        hint.id = id + '-hint';
        hint.hidden = true;
        hint.textContent = this.labels.hint;
        this.wrapper.append(hint);
        descriptions.push(hint.id);
      }
      if (descriptions.filter(Boolean).length) this.trigger.setAttribute('aria-describedby', descriptions.filter(Boolean).join(' '));
      select.hidden = true;
      select.setAttribute('aria-hidden', 'true');
      select.tabIndex = -1;

      const listenerOptions = {signal: this.signal};
      this.trigger.addEventListener('click', () => this.isOpen ? this.close() : this.open(), listenerOptions);
      this.trigger.addEventListener('keydown', event => this.onKeydown(event), listenerOptions);
      select.addEventListener('change', () => this.sync(), listenerOptions);
      this.panel.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'touch' && event.target.closest('[data-qp-picker-option]')) event.preventDefault();
      }, listenerOptions);
      this.panel.addEventListener('click', event => {
        const row = event.target.closest('[data-qp-picker-option]');
        if (row && this.panel.contains(row)) this.choose(row.dataset.value);
      }, listenerOptions);
      this.panel.addEventListener('pointermove', event => {
        const row = event.target.closest('[data-qp-picker-option]');
        const index = this.rows.indexOf(row);
        if (index !== -1 && index !== this.activeIndex) this.setActive(index, false);
      }, listenerOptions);
      document.addEventListener('pointerdown', event => {
        if (this.isOpen && !this.wrapper.contains(event.target)) this.close();
      }, {...listenerOptions, capture: true});
      document.addEventListener('focusin', event => {
        if (this.isOpen && !this.wrapper.contains(event.target)) this.close();
      }, listenerOptions);
      // Capture Escape before the owning dialog can interpret it as dismissal.
      document.addEventListener('keydown', event => {
        if (this.isOpen && event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.close(true);
        }
      }, {...listenerOptions, capture: true});
      document.addEventListener('scroll', event => {
        if (!this.isOpen || event.target === this.panel || this.panel.contains(event.target)) return;
        // Dialog opening/focus can queue a scroll event before the picker opens.
        // Dismiss only when that event reflects movement of the anchored field.
        const rect = this.trigger.getBoundingClientRect();
        if (this.anchorRect && (Math.abs(rect.top - this.anchorRect.top) > 1 || Math.abs(rect.left - this.anchorRect.left) > 1)) this.close();
      }, {...listenerOptions, capture: true, passive: true});
      window.addEventListener('resize', () => { if (this.isOpen) this.position(); }, listenerOptions);
      window.visualViewport?.addEventListener('resize', () => { if (this.isOpen) this.position(); }, listenerOptions);
      this.signal.addEventListener('abort', () => this.destroy(), {once: true});
      this.sync();
    }

    sync() {
      if (!this.wrapper || this.signal.aborted) return;
      // The form controller also writes `hidden` while resolving fixed options.
      this.select.hidden = true;
      const option = this.select.selectedOptions[0];
      this.value.textContent = option ? this.optionLabel(option) : '';
      this.trigger.disabled = this.select.disabled;
      this.trigger.setAttribute('aria-required', String(this.select.required));
      this.wrapper.hidden = this.select.parentElement.hasAttribute('data-qp-single-value');
      if (this.trigger.disabled || this.wrapper.hidden) this.close();
      // Keep open groups stable when a controller refreshes the native options.
      // The current native option is checked again when a row is chosen.
    }

    optionLabel(option) {
      return option.dataset.qpLabel || option.label || option.textContent;
    }

    eligible(option) {
      return option && !option.disabled && !option.parentElement?.disabled && option.dataset.qpAvailability !== 'missing';
    }

    render() {
      this.panel.replaceChildren();
      this.rows = [];
      const options = [...this.select.options].filter(option => this.eligible(option));
      const addRow = (option, parent) => {
        const row = document.createElement('div');
        row.id = this.panel.id + '-option-' + this.rows.length;
        row.className = 'maxler-qp-picker-option';
        row.dataset.qpPickerOption = '';
        row.dataset.value = option.value;
        row.setAttribute('role', 'option');
        const selected = option.value === this.select.value;
        row.setAttribute('aria-selected', String(selected));
        row.classList.toggle('is-selected', selected);
        const text = document.createElement('span');
        text.textContent = this.optionLabel(option);
        const tick = document.createElement('span');
        tick.className = 'maxler-qp-picker-tick';
        tick.setAttribute('aria-hidden', 'true');
        tick.textContent = selected ? '✓' : '';
        row.append(text, tick);
        parent.append(row);
        this.rows.push(row);
      };
      const placeholder = options.find(option => option.value === '');
      if (placeholder) addRow(placeholder, this.panel);
      for (const availability of ['available', 'unavailable']) {
        const groupOptions = options.filter(option => option.value !== '' && (option.dataset.qpAvailability || 'available') === availability);
        if (!groupOptions.length) continue;
        const group = document.createElement('div');
        group.setAttribute('role', 'group');
        const heading = document.createElement('div');
        heading.id = this.panel.id + '-' + availability;
        heading.className = 'maxler-qp-picker-group-label';
        heading.textContent = this.labels[availability];
        group.setAttribute('aria-labelledby', heading.id);
        group.append(heading);
        groupOptions.forEach(option => addRow(option, group));
        this.panel.append(group);
      }
      if (!this.rows.some(row => row.dataset.value !== '')) {
        const empty = document.createElement('p');
        empty.className = 'maxler-qp-picker-empty';
        empty.setAttribute('role', 'status');
        empty.textContent = this.labels.empty;
        this.panel.append(empty);
      }
      const selectedSize = [...(this.context?.querySelectorAll('[data-qp-kind="size"]') || [])]
        .some(select => select !== this.select && select.value);
      const hiddenCombinations = [...this.select.options].some(option => option.value !== '' && !this.eligible(option));
      if (selectedSize && hiddenCombinations && this.labels.hint) {
        const hint = document.createElement('p');
        hint.className = 'maxler-qp-picker-hint';
        hint.textContent = this.labels.hint;
        this.panel.append(hint);
      }
    }

    open(preferLast = false) {
      if (this.isOpen || this.signal.aborted || this.trigger.disabled || this.wrapper.hidden) return;
      this.render();
      this.panel.hidden = false;
      if (this.hasPopover) {
        try { this.panel.showPopover(); }
        catch (_) { this.panel.hidden = true; return; }
      }
      this.isOpen = true;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.position();
      const selected = this.rows.findIndex(row => row.dataset.value === this.select.value);
      this.setActive(selected === -1 ? (preferLast ? this.rows.length - 1 : 0) : selected);
      this.typeahead = '';
      this.typeaheadAt = 0;
    }

    position() {
      const rect = this.trigger.getBoundingClientRect();
      this.anchorRect = {top: rect.top, left: rect.left};
      const gap = 6;
      const gutter = 8;
      const viewport = window.visualViewport;
      const viewportLeft = viewport?.offsetLeft || 0;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportWidth = viewport?.width || window.innerWidth;
      const viewportHeight = viewport?.height || window.innerHeight;
      const width = Math.min(rect.width, Math.max(0, viewportWidth - gutter * 2));
      const below = Math.max(0, viewportTop + viewportHeight - rect.bottom - gap - gutter);
      const above = Math.max(0, rect.top - viewportTop - gap - gutter);
      const placeAbove = below < Math.min(200, this.panel.scrollHeight) && above > below;
      const maxHeight = Math.min(360, Math.max(0, viewportHeight - gutter * 2), placeAbove ? above : below);
      Object.assign(this.panel.style, {
        position: 'fixed',
        boxSizing: 'border-box',
        margin: '0',
        inset: 'auto',
        left: Math.max(viewportLeft + gutter, Math.min(rect.left, viewportLeft + viewportWidth - width - gutter)) + 'px',
        width: width + 'px',
        maxHeight: maxHeight + 'px',
        overflowY: 'auto'
      });
      const panelHeight = Math.min(this.panel.getBoundingClientRect().height, maxHeight);
      const top = placeAbove ? rect.top - gap - panelHeight : rect.bottom + gap;
      this.panel.style.top = Math.max(viewportTop + gutter, Math.min(top, viewportTop + viewportHeight - panelHeight - gutter)) + 'px';
    }

    setActive(index, scroll = true) {
      this.rows[this.activeIndex]?.classList.remove('is-active');
      this.activeIndex = this.rows.length ? Math.max(0, Math.min(index, this.rows.length - 1)) : -1;
      const row = this.rows[this.activeIndex];
      if (!row) {
        this.trigger.removeAttribute('aria-activedescendant');
        return;
      }
      row.classList.add('is-active');
      this.trigger.setAttribute('aria-activedescendant', row.id);
      if (scroll) {
        const panelRect = this.panel.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();
        if (rowRect.top < panelRect.top) this.panel.scrollTop -= panelRect.top - rowRect.top;
        else if (rowRect.bottom > panelRect.bottom) this.panel.scrollTop += rowRect.bottom - panelRect.bottom;
      }
    }

    onKeydown(event) {
      if (event.key === 'Tab') { this.close(); return; }
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        if (!this.isOpen) this.open(event.key === 'ArrowUp' || event.key === 'End');
        else if (event.key === 'ArrowDown') this.setActive(this.activeIndex + 1);
        else if (event.key === 'ArrowUp') this.setActive(this.activeIndex - 1);
        if (event.key === 'Home') this.setActive(0);
        if (event.key === 'End') this.setActive(this.rows.length - 1);
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (!this.isOpen) this.open();
        else if (this.rows[this.activeIndex]) this.choose(this.rows[this.activeIndex].dataset.value);
        return;
      }
      if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey || event.isComposing) return;
      event.preventDefault();
      if (!this.isOpen) this.open();
      const now = Date.now();
      this.typeahead = (now - this.typeaheadAt < 700 ? this.typeahead : '') + event.key.toLocaleLowerCase();
      this.typeaheadAt = now;
      const repeated = [...this.typeahead].every(char => char === this.typeahead[0]);
      const query = repeated ? this.typeahead[0] : this.typeahead;
      const start = repeated ? this.activeIndex + 1 : this.activeIndex;
      for (let offset = 0; offset < this.rows.length; offset++) {
        const index = (start + offset + this.rows.length) % this.rows.length;
        if (this.rows[index].firstElementChild.textContent.toLocaleLowerCase().startsWith(query)) {
          this.setActive(index);
          break;
        }
      }
    }

    choose(value) {
      const option = [...this.select.options].find(candidate => candidate.value === value);
      if (this.signal.aborted || this.select.disabled || !this.eligible(option)) {
        this.close(true);
        this.sync();
        return;
      }
      this.close();
      this.select.value = value;
      this.sync();
      this.trigger.focus({preventScroll: true});
      this.select.dispatchEvent(new Event('change', {bubbles: true}));
    }

    close(returnFocus = false) {
      if (!this.wrapper) return;
      this.isOpen = false;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.removeAttribute('aria-activedescendant');
      if (this.hasPopover && this.panel.matches(':popover-open')) this.panel.hidePopover();
      this.panel.hidden = true;
      if (returnFocus && this.trigger.isConnected && !this.trigger.disabled && !this.wrapper.hidden) this.trigger.focus({preventScroll: true});
    }

    destroy() {
      this.close();
      this.wrapper?.remove();
      if (!this.original) return;
      this.select.hidden = this.original.hidden;
      for (const [attribute, value] of [['aria-hidden', this.original.ariaHidden], ['tabindex', this.original.tabIndex]]) {
        if (value === null) this.select.removeAttribute(attribute);
        else this.select.setAttribute(attribute, value);
      }
      for (const {label, originalId, originalFor} of this.labelElements) {
        label.id = originalId;
        if (originalFor === null) label.removeAttribute('for');
        else label.setAttribute('for', originalFor);
      }
      if (!this.original.id) this.select.removeAttribute('id');
    }
  }

  window.MaxlerFlavourPicker = MaxlerFlavourPicker;
})();
